import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Archive,
  CaretDown,
  DotsThreeOutline,
  FolderPlus,
  PencilSimple,
  Plus,
  Warning,
} from '@phosphor-icons/react';
import { useAgentsSidebarContext } from './context';
import { CustomColorPalette } from '../../../../theme';
import { SpaceMenuPopover } from '../../LayoutComponents/Spaces/SpaceMenuPopover';

interface SectionStyledProps {
  hasBorder: boolean;
}

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: prop => prop !== 'hasBorder',
})<SectionStyledProps>(({ theme, hasBorder }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:before': {
      display: 'none',
    },
    marginBottom: hasBorder ? '8px' : '0px',
    borderBottom: hasBorder
      ? `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`
      : 'none',
    '&.Mui-expanded': {
      margin: '0 0 8px 0',
    },
  };
});

const StyledAccordionSummary = styled(AccordionSummary)({
  minHeight: '32px',
  maxHeight: '32px',
  padding: '0 8px',
  '&.Mui-expanded': {
    minHeight: '32px',
    maxHeight: '32px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0 4px 0',
    overflow: 'hidden',
  },
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColorPalette.gray[500],
  fontFamily: 'Roboto',
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '13px',
    fontFamily: 'Roboto',
    color: isDarkMode ? palette.gray[200] : palette.gray[700],
    gap: '8px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
    },
  };
});

const DeleteMenuItem = styled(MenuItem)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '13px',
    fontFamily: 'Roboto',
    color: isDarkMode ? '#ef4444' : '#dc2626',
    gap: '8px',
    '&:hover': {
      backgroundColor: isDarkMode
        ? 'rgba(239,68,68,0.1)'
        : 'rgba(220,38,38,0.1)',
    },
  };
});

const menuPaperSx = (isDarkMode: boolean, palette: CustomColorPalette) => ({
  padding: '8px',
  backgroundColor: isDarkMode ? palette.gray[900] : '#FFFFFF',
  minWidth: '180px',
  borderRadius: '8px',
  border: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[300]}`,
  boxShadow: 'none',
});

interface SectionProps {
  title: string;
  children: React.ReactNode;
  hasBorder?: boolean;
  isAccordion?: boolean;
  defaultExpanded?: boolean;
  isDisabled?: boolean;
  onAddFolder?: () => void;
  onArchive?: () => void;
  onEditSpace?: () => void;
  onAddUsers?: () => void;
  onCopySpaceId?: () => void;
}

export const Section: React.FC<SectionProps> = ({
  title,
  children,
  hasBorder = false,
  isAccordion = false,
  defaultExpanded = true,
  isDisabled = false,
  onAddFolder,
  onArchive,
  onEditSpace,
  onAddUsers,
  onCopySpaceId,
}) => {
  const { isDarkMode, palette } = useAgentsSidebarContext();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const isSpaceMenu = title !== 'private';
  const hasContextActions = isSpaceMenu
    ? !!onEditSpace || !!onAddUsers || !!onCopySpaceId
    : !!onAddFolder || !!onArchive || !!onEditSpace;

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const iconButtonSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    cursor: 'pointer' as const,
    color: isDarkMode ? palette.gray[400] : palette.gray[500],
    marginRight: '2px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
    },
  };

  if (isAccordion) {
    return (
      <>
        <StyledAccordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          hasBorder={hasBorder}
        >
          <StyledAccordionSummary
            expandIcon={
              <CaretDown
                size={16}
                color={palette.gray[500]}
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            }
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflow: 'hidden',
                maxWidth: '100%',
                flex: 1,
                minWidth: 0,
              }}
            >
              {isDisabled && (
                <Tooltip
                  title="This space is disabled. Go to Settings to enable it."
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Warning size={14} color="#FFC107" weight="fill" />
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={title} arrow placement="top">
                <SectionTitle>{title}</SectionTitle>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  marginLeft: 'auto',
                  flexShrink: 0,
                }}
              >
                {(isHovered || menuAnchor) && onAddFolder && (
                  <Box
                    component="button"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onAddFolder();
                    }}
                    sx={iconButtonSx}
                  >
                    <Plus size={16} color={isDarkMode ? palette.gray[400] : palette.gray[500]} />
                  </Box>
                )}
                {(isHovered || menuAnchor) && hasContextActions && (
                  <Box
                    component="button"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setMenuAnchor(e.currentTarget as HTMLElement);
                    }}
                    sx={iconButtonSx}
                  >
                    <DotsThreeOutline size={16} weight="fill" color={isDarkMode ? palette.gray[400] : palette.gray[500]} />
                  </Box>
                )}
              </Box>
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails
            sx={{
              padding: '0 8px 8px 8px',
              paddingBottom: hasBorder ? '8px' : '8px',
            }}
          >
            {children}
          </AccordionDetails>
        </StyledAccordion>
        {hasContextActions &&
          (isSpaceMenu ? (
            <SpaceMenuPopover
              key={`space-menu-${title}`}
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={handleMenuClose}
              onEditSpace={onEditSpace}
              onAddUsers={onAddUsers}
              onCopySpaceId={onCopySpaceId}
            />
          ) : (
            <Menu
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: menuPaperSx(isDarkMode, palette),
                },
              }}
            >
              {onEditSpace && (
                <StyledMenuItem
                  onClick={() => {
                    handleMenuClose();
                    onEditSpace();
                  }}
                >
                  <PencilSimple size={16} />
                  Edit Space
                </StyledMenuItem>
              )}
              {onAddFolder && (
                <StyledMenuItem
                  onClick={() => {
                    handleMenuClose();
                    onAddFolder();
                  }}
                >
                  <FolderPlus size={16} />
                  Add Folder
                </StyledMenuItem>
              )}
              {onArchive && !isDisabled && (
                <DeleteMenuItem
                  onClick={() => {
                    handleMenuClose();
                    onArchive();
                  }}
                >
                  <Archive size={16} />
                  Archive
                </DeleteMenuItem>
              )}
            </Menu>
          ))}
      </>
    );
  }

  return (
    <>
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          paddingBottom: '8px',
          marginBottom: '8px',
          borderBottom: hasBorder
            ? `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`
            : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 8px 4px 8px',
          }}
        >
          <SectionTitle sx={{ flex: 1, minWidth: 0 }}>{title}</SectionTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {(isHovered || menuAnchor) && onAddFolder && (
              <Box
                component="button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onAddFolder();
                }}
                sx={iconButtonSx}
              >
                <Plus size={16} color={isDarkMode ? palette.gray[400] : palette.gray[500]} />
              </Box>
            )}
            {(isHovered || menuAnchor) && hasContextActions && (
              <Box
                component="button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget as HTMLElement);
                }}
                sx={iconButtonSx}
              >
                <DotsThreeOutline size={16} weight="fill" color={isDarkMode ? palette.gray[400] : palette.gray[500]} />
              </Box>
            )}
          </Box>
        </Box>
        {children}
      </Box>
      {hasContextActions &&
        (isSpaceMenu ? (
          <SpaceMenuPopover
            key={`space-menu-${title}`}
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={handleMenuClose}
            onEditSpace={onEditSpace}
            onAddUsers={onAddUsers}
            onCopySpaceId={onCopySpaceId}
          />
        ) : (
          <Menu
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: menuPaperSx(isDarkMode, palette),
              },
            }}
          >
            {onEditSpace && (
              <StyledMenuItem
                onClick={() => {
                  handleMenuClose();
                  onEditSpace();
                }}
              >
                <PencilSimple size={16} />
                Edit Space
              </StyledMenuItem>
            )}
            {onAddFolder && (
              <StyledMenuItem
                onClick={() => {
                  handleMenuClose();
                  onAddFolder();
                }}
              >
                <FolderPlus size={16} />
                Add Folder
              </StyledMenuItem>
            )}
            {onArchive && !isDisabled && (
              <DeleteMenuItem
                onClick={() => {
                  handleMenuClose();
                  onArchive();
                }}
              >
                <Archive size={16} />
                Archive
              </DeleteMenuItem>
            )}
          </Menu>
        ))}
    </>
  );
};
